/** PLANUL ORGANIZĂRII DE ȘANTIER pe planșa reală de situație (`SitePlanView`:
 *  hârtia, lotul cu cote, corpurile, aleile, parcările, verdele), cu
 *  obiectele organizării desenate peste, pe faza în care cade săptămâna
 *  aleasă: gropile deschise și plăcile turnate, gardul, porțile, spălarea,
 *  drumul, barăcile, depozitele, macaralele cu raza brațului, direcția
 *  săpăturii. Totul vine din documentul planificării (`santier.ts`); aici
 *  nu se decide nimic, se desenează. */
import { useMemo } from "react";
import { data, numar, useT } from "../../i18n";
import type { Cheie } from "../../i18n";
import { laOrigineLocala, northUp, origineDesen, punctInCadru, toSiteLayer } from "../canonical";
import type { CanonicalDoc } from "../canonical";
import SitePlanView from "../plan/SitePlanView";
import type { Program } from "../cost/program";
import { Lamurire } from "../cost/Termeni";
import { camioaneleLa, fazaLa, macaraleleLa, organizareaProvizorie, type OrganizareSantier } from "./santier";
import "../viewer.css";

type Pt = [number, number];
const P = {
  gard: "#3f3f3f", poartaC: "#d94f2b", poartaP: "#2b7bd9", baraca: "#5f7f98", spalare: "#4f7ea3", macara: "#b8860b",
  groapa: "#8c7658", placa: "#9aa6b0", drum: "#8d8a80", rampa: "#a08a68", corp: "#7d8793", sapatura: "#d94f2b",
  depozit: { rebar: "#6d5236", formwork: "#9c8a5c", precast: "#8a8f99", materials: "#b89a72", waste: "#8a8a8a", concrete_pump: "#6b7f6b" } as Record<string, string>,
};
const d = (pts: Pt[], inchis = true) => "M" + pts.map((p) => p.join(" ")).join(" L") + (inchis ? " Z" : "");

export default function PlanOrganizare({ sit, doc, program, luni }: {
  sit: CanonicalDoc; doc: OrganizareSantier; program: Program; luni: string;
}) {
  const t = useT();
  const cadru = useMemo(() => {
    const nu = northUp(sit);
    const origine = origineDesen(nu);
    const local = laOrigineLocala(nu, origine);
    const site = toSiteLayer(local);
    const inCadru = punctInCadru(sit.crs, origine);
    const ob = (local as unknown as { objects: { type: string; properties?: Record<string, unknown> | null; geometry?: { poly?: Pt[] } | null }[] }).objects ?? [];
    const subsoluri: Record<string, Pt[]> = {};
    const corpuri: Record<string, Pt[]> = {};
    for (const o of ob) {
      const pr = o.properties ?? {};
      if (o.type === "Area" && pr.kind === "basement" && o.geometry?.poly) subsoluri[String(pr.code ?? "")] = o.geometry.poly;
      if (o.type === "Building" && o.geometry?.poly) corpuri[String(pr.code ?? "")] = o.geometry.poly;
    }
    const lot = (site?.boundary ?? []) as Pt[];
    const xs = lot.map((p) => p[0]), ys = lot.map((p) => p[1]);
    return { site, lot, inCadru: (p: number[]) => inCadru(p) as Pt, subsoluri, corpuri,
             b: { x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys) } };
  }, [sit]);
  const faza = fazaLa(doc, luni);
  const macarale = macaraleleLa(doc, luni);
  const camioane = camioaneleLa(doc, luni);
  const provizoriu = organizareaProvizorie(doc, luni);
  if (!cadru.site || !faza) return null;
  const { inCadru, b, lot } = cadru;
  const m = 18;
  const poly = (pts: number[][]) => pts.map(inCadru);
  const centru = (pts: Pt[]): Pt => [pts.reduce((a, p) => a + p[0], 0) / pts.length, pts.reduce((a, p) => a + p[1], 0) / pts.length];
  const FEL: Record<string, Cheie> = {
    office: "tl.plan.fel.office", locker: "tl.plan.fel.locker", wc: "tl.plan.fel.wc", canteen: "tl.plan.fel.canteen",
    first_aid: "tl.plan.fel.first_aid", guard: "tl.plan.fel.guard", rebar: "tl.plan.fel.rebar", formwork: "tl.plan.fel.formwork",
    precast: "tl.plan.fel.precast", materials: "tl.plan.fel.materials", waste: "tl.plan.fel.waste", concrete_pump: "tl.plan.fel.concrete_pump",
  };
  return (
    <div className="tl-plan">
      <svg className="tl-plan-svg" viewBox={`${b.x0 - m} ${b.y0 - m} ${b.x1 - b.x0 + 2 * m} ${b.y1 - b.y0 + 2 * m}`} preserveAspectRatio="xMidYMid meet">
        <rect x={b.x0 - 1e3} y={b.y0 - 1e3} width={2e3 + b.x1 - b.x0} height={2e3 + b.y1 - b.y0} fill="var(--paper, #ebeef1)" />
        <SitePlanView site={cadru.site} lot={lot} pxPerM={6} showCompass showSheet={false} arataCote={false} />
        <g>
          {Object.entries(cadru.subsoluri).map(([cod, pts]) => {
            const st = faza.subsoluri[cod];
            if (!st || st === "teren") return null;
            return <path key={cod} d={d(pts)} fill={st === "groapa" ? P.groapa : P.placa} fillOpacity={st === "groapa" ? 0.55 : 0.35}
                         stroke={st === "groapa" ? P.groapa : P.placa} strokeWidth={0.35} strokeDasharray={st === "groapa" ? "1.6 1" : undefined} />;
          })}
          {faza.corpuri_in_lucru.map((cod) => cadru.corpuri[cod] && <path key={cod} d={d(cadru.corpuri[cod])} fill={P.corp} fillOpacity={0.75} />)}
          {doc.sapaturi.filter((s) => faza.subsoluri[s.unitate] === "groapa").map((s) => {
            const a = inCadru(s.de_la), c = inCadru([s.de_la[0] + s.spre[0] * 30, s.de_la[1] + s.spre[1] * 30]);
            return <g key={s.unitate}>
              {s.rampa && <path d={d(poly(s.rampa.poly))} fill={P.rampa} fillOpacity={0.85} />}
              <path d={d([a, c], false)} fill="none" stroke={P.sapatura} strokeWidth={1.2} /><circle cx={a[0]} cy={a[1]} r={2} fill={P.sapatura} />
            </g>;
          })}
          {provizoriu && <>
            <path d={d(poly(doc.gard.axis), false)} fill="none" stroke={P.gard} strokeWidth={0.5} strokeDasharray="2.4 1.2" />
            {doc.drumuri.map((r) => <path key={r.id} d={d(poly(r.axis), false)} fill="none" stroke={P.drum} strokeWidth={r.latime_m} strokeOpacity={0.6} />)}
            {doc.porti.map((p) => <path key={p.id} d={d(poly(p.axis), false)} fill="none" stroke={p.fel === "vehicle" ? P.poartaC : P.poartaP} strokeWidth={1.6} />)}
            <path d={d(poly(doc.spalare_roti.poly))} fill={P.spalare} fillOpacity={0.85} />
            <path d={d(poly(doc.paza.poly))} fill="#fff" stroke={P.baraca} strokeWidth={0.3} />
          </>}
          {faza.blocuri_baraci.map((bl, i) => <path key={"b" + i} d={d(poly(bl))} fill="#dfe6ec" fillOpacity={0.6} stroke={P.baraca} strokeWidth={0.25} strokeDasharray="1 0.6" />)}
          {faza.baraci.filter((c) => !c.etaj).map((c) => <path key={c.id} d={d(poly(c.poly))} fill="#fff" stroke={P.baraca} strokeWidth={0.25} />)}
          {faza.depozite.filter((x) => x.poly).map((x) => {
            const pts = poly(x.poly!); const c = centru(pts);
            return <g key={x.id}><path d={d(pts)} fill={P.depozit[x.fel] ?? "#999"} fillOpacity={0.85} />
              <text x={c[0]} y={c[1] + 1} fontSize={2.6} fill="#fff" textAnchor="middle">{t(FEL[x.fel])}</text></g>;
          })}
          {macarale.map((mc) => {
            const [x, y] = inCadru([mc.x, mc.y]);
            return <g key={mc.id}>
              <circle cx={x} cy={y} r={mc.raza_m} fill={P.macara} fillOpacity={0.05} stroke={P.macara} strokeWidth={0.35} strokeDasharray="2 1.4" />
              <rect x={x - 2.5} y={y - 2.5} width={5} height={5} fill="#f0b400" stroke={P.macara} strokeWidth={0.4} />
              <line x1={x} y1={y} x2={x} y2={y - mc.raza_m} stroke={P.macara} strokeWidth={0.5} />
              <text x={x + 3.2} y={y - 2.5} fontSize={3.2} fill="#7a5a00">{mc.unitate}</text>
            </g>;
          })}
        </g>
      </svg>
      <aside className="tl-plan-panou">
        <div className="tl-panou-cap"><b>{t("tl.plan.faza", { id: faza.id })}</b>
          <Lamurire text={t("tl.plan.sursa", { n: program.macarale.plafon })} />
          <span className="ip-pill ip-pill-mut">{data(faza.de_la)} → {data(faza.pana_la)}</span></div>
        <div className="tl-sub">{faza.motiv}</div>
        <div className="tl-kpi">
          <div><span className="ob-lbl">{t("tl.plan.oameni_varf")}</span><b>{numar(faza.oameni_varf)}</b></div>
          <div><span className="ob-lbl">{t("tl.plan.curte")}</span><b>{numar(faza.curte_m2)} <small>m²</small></b></div>
          <div><span className="ob-lbl">{t("tl.camioane")}</span><b>{camioane ? numar(camioane.pe_zi, { maximumFractionDigits: 0 }) : "—"}</b></div>
        </div>
        <div className="ob-lbl" style={{ marginTop: 8 }}>{t("tl.plan.containere")}{faza.etaje_baraci > 1 ? ` · ${t("tl.plan.etaje", { n: faza.etaje_baraci })}` : ""}</div>
        <div className="tl-plan-lista">
          {faza.containere.filter((c) => c.numar).map((c) => <span key={c.fel}>{c.numar} × {t(FEL[c.fel] ?? "tl.plan.fel.office")}</span>)}
        </div>
        {faza.mutare && <div className="tl-explicatie">{faza.mutare.nume} · {t("tl.plan.mutare", { zile: faza.mutare.zile, oameni: faza.mutare.oameni })}</div>}
        <div className="ob-lbl" style={{ marginTop: 8 }}>{t("tl.plan.macarale")}</div>
        <div className="tl-plan-lista">
          {macarale.length === 0 && <span className="tl-gol">—</span>}
          {macarale.map((mc) => <span key={mc.id}>{mc.unitate} · {mc.raza_m} m · {t(mc.in_groapa ? "tl.plan.in_groapa" : mc.pe_placa ? "tl.plan.pe_placa" : "tl.plan.in_curte")}
            {mc.peste_vecini.length ? ` · ${t("tl.plan.peste_vecini")}` : ""}</span>)}
        </div>
        <div className="tl-sub" style={{ marginTop: 6 }}>{t("tl.plan.incap", { n: doc.incap.catarge, d: doc.incap.distanta_m, plafon: doc.incap.plafon_program })}</div>
        {doc.avertismente.length > 0 && <>
          <div className="ob-lbl" style={{ marginTop: 8 }}>{t("tl.plan.avertismente")}</div>
          <div className="tl-plan-lista tl-plan-avert">{doc.avertismente.slice(0, 6).map((a, i) => <span key={i}>{a}</span>)}</div>
        </>}
      </aside>
    </div>
  );
}
